import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { autoHyphen } from '@utils/common';
import { petInfoTableHeader } from '@lib/data';
import { SEE_USER_DETAIL } from '@graphql/queries';
import { DELETE_USER } from '@graphql/mutations';
import { Query, Mutation, Pet } from '@graphql/types';
import { getLoginType } from '@lib/enum';

import Headline from '@components/layout/Headline';
import {
    DetailSection,
    DetailDataRow,
    InfoTitle
} from '@components/share/DetailPage/DetailSection';
import {
    Contents,
    FlexBox,
    RegularFont
} from '@components/styled/StyledComponents';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import PetInfoTableRow from '@components/feature/Member/Status/Detail/PetInfoTableRow';
import UpdateUserModal from '@components/feature/Member/Status/Detail/UpdateUserModal';
import Loader from '@components/share/Loader';

export type UpdateUserInfoProps = {
    userId: number | null;
    name: string;
    cellphone: string;
    email: string;
};

const MemberStatusDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const { data, loading } = useQuery<Pick<Query, 'seeUserDetail'>>(
        SEE_USER_DETAIL,
        {
            variables: {
                userId: +(id as string)
            },
            onError: (err) => {
                console.error(err);
            }
        }
    );
    const [deleteUser] = useMutation<Pick<Mutation, 'deleteUser'>>(DELETE_USER);

    const {
        userId,
        uniqueKeyId,
        createdAt,
        email,
        name,
        cellphone,
        snsType,
        qrCount,
        pet
    } = data?.seeUserDetail ?? {};

    const onDelete = async () => {
        if (!window.confirm('해당 회원을 삭제하시겠습니까?')) return;
        try {
            await deleteUser({
                variables: {
                    userId
                },
                onCompleted: () => {
                    toast.success('회원이 삭제되었습니다.');
                    navigate('/member/status');
                }
            });
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <Loader />;

    return (
        <Contents>
            <Headline
                title="회원 상세 정보"
                subTitle="펫이닷 회원 상세 정보 조회"
                buttonList={['수정', '삭제']}
                buttonAction={[() => setOpenUpdateModal(true), onDelete]}
                DETAIL
            />
            <DetailSection>
                <FlexBox $alignItems="flex-start">
                    <InfoTitle>기본정보</InfoTitle>
                    <FlexBox $flexDirection="column" $alignItems="flex-start">
                        <DetailDataRow
                            title="등록일"
                            value={
                                createdAt
                                    ? dayjs(createdAt).format(
                                          'YYYY-MM-DD HH:mm'
                                      )
                                    : '-'
                            }
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="ID"
                            value={uniqueKeyId || '-'}
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="이름"
                            value={name || '-'}
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="소셜 로그인 연동"
                            value={
                                typeof snsType === 'number'
                                    ? (getLoginType(snsType) as string)
                                    : '-'
                            }
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="휴대폰 번호"
                            value={cellphone ? autoHyphen(cellphone) : '-'}
                            margin="0 0 20px"
                        />
                        <DetailDataRow
                            title="이메일"
                            value={email || '-'}
                            margin="0 0 20px"
                        />
                    </FlexBox>
                </FlexBox>
            </DetailSection>
            <DetailSection>
                <FlexBox $alignItems="flex-start">
                    <InfoTitle>등록 QR 정보</InfoTitle>
                    <FlexBox $flexDirection="column" $alignItems="flex-start">
                        <RegularFont
                            $fontSize={15}
                            $margin="0 0 32px"
                        >{`총 ${qrCount}개의 QR 등록됨`}</RegularFont>
                        <TableHeader header={petInfoTableHeader} />
                        <TableBody $height={200}>
                            {qrCount === 0 ? (
                                <EmptyTable>
                                    반려동물 정보가 없습니다.
                                </EmptyTable>
                            ) : (
                                (pet as Pet[]).map((data, idx) => (
                                    <PetInfoTableRow
                                        key={`${data?.petId}-${idx}-pet-info-list`}
                                        header={petInfoTableHeader}
                                        data={data}
                                    />
                                ))
                            )}
                        </TableBody>
                    </FlexBox>
                </FlexBox>
            </DetailSection>
            {openUpdateModal && (
                <UpdateUserModal
                    isOpen={openUpdateModal}
                    setIsOpen={setOpenUpdateModal}
                    data={
                        {
                            userId,
                            name,
                            cellphone,
                            email
                        } as UpdateUserInfoProps
                    }
                />
            )}
        </Contents>
    );
};

export default MemberStatusDetail;
